import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { InboxList } from '../../components/notifications/InboxList';
import { TodoList } from '../../components/notifications/TodoList';
import { useNotificationCounts } from '../../services/queries/useNotificationCounts';
import type { NotificationsNavigatorParamList } from '../../navigation/NotificationsNavigator';

type Segment = 'inbox' | 'todo';

const SEGMENTS: { key: Segment; labelKey: string }[] = [
  { key: 'inbox', labelKey: 'tabs.inbox' },
  { key: 'todo', labelKey: 'notifications.todo' },
];

interface Props {
  navigation: StackNavigationProp<NotificationsNavigatorParamList, 'NotificationsHome'>;
}

export function NotificationsHomeScreen({ navigation }: Props) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [segment, setSegment] = useState<Segment>('inbox');
  // Same combined-counts query the tab bar badge uses; per-segment numbers
  // come for free and stay in sync after mutations invalidate the cache.
  const { data: counts } = useNotificationCounts();
  const segmentBadges: Record<Segment, number> = {
    inbox: counts?.inbox_unread ?? 0,
    todo: counts?.actions_open ?? 0,
  };

  const openItem = (itemId: string) => {
    navigation.navigate('InboxReading', { itemId });
  };

  return (
    <View style={styles.container}>
      <View style={styles.segmented}>
        {SEGMENTS.map((s) => {
          const active = s.key === segment;
          const badge = segmentBadges[s.key];
          return (
            <TouchableOpacity
              key={s.key}
              style={[styles.segment, active && styles.segmentActive]}
              onPress={() => setSegment(s.key)}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
            >
              <View style={styles.segmentContent}>
                <Text style={[styles.segmentText, active && styles.segmentTextActive]}>
                  {t(s.labelKey)}
                </Text>
                {badge > 0 && (
                  <View style={[styles.badge, active && styles.badgeActive]}>
                    <Text style={[styles.badgeText, active && styles.badgeTextActive]}>
                      {badge > 99 ? '99+' : badge}
                    </Text>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
      <View testID="inbox-wrapper" style={[styles.subView, segment !== 'inbox' && styles.hidden]}>
        <InboxList onOpenItem={openItem} />
      </View>
      <View testID="todo-wrapper" style={[styles.subView, segment !== 'todo' && styles.hidden]}>
        <TodoList />
      </View>
    </View>
  );
}

function createStyles(theme: any) {
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: theme.colors.background },
    segmented: {
      flexDirection: 'row',
      margin: theme.spacing.md,
      borderRadius: theme.radii.md,
      borderWidth: 1,
      borderColor: theme.colors.border,
      overflow: 'hidden',
    },
    segment: { flex: 1, paddingVertical: theme.spacing.sm, alignItems: 'center' },
    segmentActive: { backgroundColor: theme.colors.primary },
    segmentText: { color: theme.colors.textSecondary, fontSize: theme.typography.fontSizeSm },
    segmentTextActive: {
      color: theme.colors.textInverse,
      fontWeight: theme.typography.fontWeightBold,
    },
    segmentContent: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.xs },
    badge: {
      minWidth: 18,
      height: 18,
      paddingHorizontal: theme.spacing.xxs,
      borderRadius: 9,
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    badgeActive: { backgroundColor: theme.colors.background },
    badgeText: {
      color: theme.colors.textInverse,
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightBold,
    },
    badgeTextActive: { color: theme.colors.primary },
    subView: { flex: 1 },
    hidden: { display: 'none' },
  });
}
